// JumpBean constants
export * from "./referrals.js";
// ==================== Discipline Constants ====================
export const DISCIPLINE_SLUGS = {
    MATH: "math",
    SPELLING: "spelling",
    PATTERNS: "patterns",
    TYPING: "typing",
    PROGRAMMING: "programming",
};
export const LEVELS_PER_DISCIPLINE = 5;
export const CHALLENGES_PER_LEVEL = 10;
export const MAX_STARS_PER_CHALLENGE = 3;
// ==================== Subscription Constants ====================
export const CHILD_LIMITS = {
    FREE: 1,
    FAMILY: 4,
    CLASSROOM: 35,
};
export const SUBSCRIPTION_PRICES = {
    FAMILY_MONTHLY: 500, // $5.00 in cents
    FAMILY_ANNUAL: 4800, // $48.00 in cents
    CLASSROOM_MONTHLY: 2900, // $29.00 in cents
    CLASSROOM_ANNUAL: 29000, // $290.00 in cents
};
// ==================== Color Constants ====================
export const COLORS = {
    // Primary Colors
    sunshineYellow: "#FFD93D",
    skyBlue: "#6BCB77",
    fairyPink: "#FF6B9D",
    magicPurple: "#9B59B6",
    cloudWhite: "#FFFFFF",
    // Discipline Colors
    math: "#E8A0FF",
    spelling: "#90EE90",
    patterns: "linear-gradient(135deg, #FF6B6B, #FFE66D, #4ECDC4, #A8E6CF, #FF6B9D)",
    typing: "#4A90D9",
    programming: "#FF8C42",
};
// ==================== Character Constants ====================
export const CHARACTERS = {
    LUNA: {
        name: "Luna the Unicorn",
        discipline: "math",
        description: "Purple/pink, sparkly unicorn who guides children through counting and math",
    },
    PETAL: {
        name: "Petal the Fairy",
        discipline: "spelling",
        description: "Fairy with green wings and flower crown for spelling adventures",
    },
    PRISM: {
        name: "Prism the Dragon",
        discipline: "patterns",
        description: "Small, friendly, rainbow-colored dragon for pattern puzzles",
    },
    WHISKERS: {
        name: "Whiskers the Wizard Cat",
        discipline: "typing",
        description: "Blue-robed cat with a tiny wand for typing practice",
    },
    PIXEL: {
        name: "Pixel the Robot Owl",
        discipline: "programming",
        description: "Friendly owl with gear decorations for coding adventures",
    },
};
// ==================== Discipline Display Data ====================
/**
 * Static discipline data for display purposes.
 * This mirrors what would be in the database but is available for UI rendering
 * before API data is loaded.
 */
export const DISCIPLINES = {
    math: {
        slug: "math",
        name: "Math",
        description: "Counting, numbers, and basic math with Luna the Unicorn",
        characterName: "Luna the Unicorn",
        displayOrder: 1,
    },
    spelling: {
        slug: "spelling",
        name: "Spelling",
        description: "Letters, sounds, and words with Petal the Fairy",
        characterName: "Petal the Fairy",
        displayOrder: 2,
    },
    patterns: {
        slug: "patterns",
        name: "Patterns",
        description: "Colors, shapes, and sequences with Prism the Dragon",
        characterName: "Prism the Dragon",
        displayOrder: 3,
    },
    typing: {
        slug: "typing",
        name: "Typing",
        description: "Keyboard skills with Whiskers the Wizard Cat",
        characterName: "Whiskers the Wizard Cat",
        displayOrder: 4,
    },
    programming: {
        slug: "programming",
        name: "Programming",
        description: "Code adventures with Pixel the Robot Owl",
        characterName: "Pixel the Robot Owl",
        displayOrder: 5,
    },
};
// ==================== Badge Constants ====================
/**
 * Math badges - earned by completing each level
 */
export const MATH_BADGES = [
    { level: 1, name: "Counting Star", description: "Mastered basic counting" },
    {
        level: 2,
        name: "Number Explorer",
        description: "Explored extended counting",
    },
    {
        level: 3,
        name: "Addition Apprentice",
        description: "Learned number recognition",
    },
    { level: 4, name: "Math Whiz", description: "Conquered simple addition" },
    {
        level: 5,
        name: "Castle Champion",
        description: "Mastered all math challenges",
    },
];
/**
 * Spelling badges - earned by completing each level
 */
export const SPELLING_BADGES = [
    {
        level: 1,
        name: "Letter Learner",
        description: "Identified letters A-M",
    },
    {
        level: 2,
        name: "Alphabet Ace",
        description: "Identified letters N-Z",
    },
    {
        level: 3,
        name: "Sound Seeker",
        description: "Learned beginning sounds",
    },
    {
        level: 4,
        name: "Word Wizard",
        description: "Mastered vowel sounds",
    },
    {
        level: 5,
        name: "Spelling Fairy",
        description: "Can spell 3-letter words",
    },
];
/**
 * Pattern badges - earned by completing each level
 */
export const PATTERN_BADGES = [
    {
        level: 1,
        name: "Pattern Spotter",
        description: "Recognized 2-color patterns",
    },
    {
        level: 2,
        name: "Sequence Seeker",
        description: "Mastered 3-color patterns",
    },
    {
        level: 3,
        name: "Shape Sorter",
        description: "Completed shape patterns",
    },
    {
        level: 4,
        name: "Pattern Pro",
        description: "Understood size patterns",
    },
    {
        level: 5,
        name: "Rainbow Master",
        description: "Conquered mixed patterns",
    },
];
/**
 * Typing badges - earned by completing each level
 */
export const TYPING_BADGES = [
    {
        level: 1,
        name: "Key Finder",
        description: "Found single letters on keyboard",
    },
    {
        level: 2,
        name: "Letter Catcher",
        description: "Typed letters in sequence",
    },
    {
        level: 3,
        name: "Word Weaver",
        description: "Mastered key sequences",
    },
    {
        level: 4,
        name: "Typing Trainee",
        description: "Typed simple words",
    },
    {
        level: 5,
        name: "Keyboard King",
        description: "Typed short phrases",
    },
];
/**
 * Programming badges - earned by completing each level
 */
export const PROGRAMMING_BADGES = [
    {
        level: 1,
        name: "Command Cadet",
        description: "Learned single direction commands",
    },
    {
        level: 2,
        name: "Sequence Starter",
        description: "Mastered multiple commands",
    },
    {
        level: 3,
        name: "Maze Navigator",
        description: "Navigated simple mazes",
    },
    {
        level: 4,
        name: "Code Collector",
        description: "Collected items in mazes",
    },
    {
        level: 5,
        name: "Programming Pro",
        description: "Learned loop concepts",
    },
];
/**
 * All badges organized by discipline
 */
export const BADGES = {
    math: MATH_BADGES,
    spelling: SPELLING_BADGES,
    patterns: PATTERN_BADGES,
    typing: TYPING_BADGES,
    programming: PROGRAMMING_BADGES,
};
/**
 * Get badge info for a specific discipline and level
 */
export function getBadgeInfo(discipline, level) {
    const badges = BADGES[discipline];
    return badges.find((b) => b.level === level) ?? null;
}
// ==================== Avatar Constants ====================
export const AVATARS = [
    { id: "bunny", name: "Bunny", emoji: "🐰" },
    { id: "bear", name: "Bear", emoji: "🐻" },
    { id: "cat", name: "Cat", emoji: "🐱" },
    { id: "dog", name: "Dog", emoji: "🐶" },
    { id: "fox", name: "Fox", emoji: "🦊" },
    { id: "panda", name: "Panda", emoji: "🐼" },
    { id: "lion", name: "Lion", emoji: "🦁" },
    { id: "unicorn", name: "Unicorn", emoji: "🦄" },
];
//# sourceMappingURL=index.js.map